'use client';

import { useCallback, useEffect, useState } from 'react';
export { CurrencySymbol } from 'components/shared/CurrencySymbol';
import { Currency } from 'utils/Currency';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
// Re-export the centralized string functions for convenience
export {
  getCurrencySymbolFromEnum,
  getCurrencySymbolFromString,
} from 'lib/region/countryUtils';

// INR is the only currency that groups in lakhs; every other currency groups
// in thousands. Below 100,000 the two are identical, which is why this only
// surfaced once JPY brought prices above that line.
const getGroupingLocale = (currency?: Currency) =>
  !currency || currency === Currency.inr ? 'en-IN' : 'en-US';

export const formatPriceWithComma = (input: number, currency?: Currency) => {
  // Truncate to 2 decimal places without rounding
  const truncated = Math.floor(Math.round(input * 100)) / 100;
  const hasFraction = truncated % 1 !== 0;
  return truncated?.toLocaleString(getGroupingLocale(currency), {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

// Currencies with no minor unit - never render a decimal part for these.
const ZERO_DECIMAL_CURRENCIES = new Set<Currency>([Currency.jpy, Currency.krw]);

export const hasMinorUnit = (currency?: Currency) =>
  !currency || !ZERO_DECIMAL_CURRENCIES.has(currency);

/**
 * Region-aware `formatPriceWithComma`. Same call signature, so a component can
 * swap its import for this hook and leave every call site untouched.
 */
export const useFormatPrice = () => {
  const { currency } = useRegionLocale();
  return useCallback(
    (input: number) => formatPriceWithComma(input, currency),
    [currency],
  );
};

const getLanguage = () => {
  if (navigator.languages != undefined) {
    return navigator.languages[0];
  }
  return navigator.language;
};

export const useFormattedPrice = ({
  currency,
  price,
}: {
  currency: Currency;
  price: number;
}) => {
  const [priceFormatted, setPriceFormatted] = useState(
    `${currency.toUpperCase()} ${price}`,
  );

  useEffect(() => {
    try {
      setPriceFormatted(
        new Intl.NumberFormat(getLanguage(), {
          style: 'currency',
          currency: currency,
          maximumFractionDigits: 0,
        }).format(price),
      );
    } catch {}
  }, [currency, price]);

  return priceFormatted;
};
